import headerBackgroundImageSize from 'config/sectionHeader/headerBackgroundImageSize';
import headerBackgroundMoveDirections from 'config/sectionHeader/headerBackgroundMoveDirections';
import toPx from 'utils/toPx';

const isBackgroundSizeCover = backgroundImageSize =>
  backgroundImageSize === headerBackgroundImageSize.cover;
const isBackgroundSizeContain = backgroundImageSize =>
  backgroundImageSize === headerBackgroundImageSize.contain;
const isBackgroundSizeRepeat = backgroundImageSize =>
  backgroundImageSize === headerBackgroundImageSize.repeat;
const isBackgroundSizeCoverOrContain = backgroundImageSize =>
  isBackgroundSizeCover(backgroundImageSize) ||
  isBackgroundSizeContain(backgroundImageSize);

const isMoveDirectionVertical = imageMoveDirection =>
  imageMoveDirection === headerBackgroundMoveDirections.vertical;
const isMoveDirectionHorizontal = imageMoveDirection =>
  imageMoveDirection === headerBackgroundMoveDirections.horizontal;
const isMoveDirectionAny = imageMoveDirection =>
  imageMoveDirection === headerBackgroundMoveDirections.any;

const calculateWidthScale = (containerWidth, imageWidth) =>
  containerWidth / imageWidth;
const calculateHeightScale = (containerHeight, imageHeight) =>
  containerHeight / imageHeight;

const calculateMoveDirection = (
  backgroundImageSize,
  containerSize,
  imageSize,
) => {
  const { height: containerHeight, width: containerWidth } = containerSize;
  const { height: imageHeight, width: imageWidth } = imageSize;
  const isWidthScaleBigger =
    calculateWidthScale(containerWidth, imageWidth) >
    calculateHeightScale(containerHeight, imageHeight);

  let imageMoveDirection = headerBackgroundMoveDirections.any;

  if (isBackgroundSizeCover(backgroundImageSize)) {
    imageMoveDirection = isWidthScaleBigger
      ? headerBackgroundMoveDirections.vertical
      : headerBackgroundMoveDirections.horizontal;
  } else if (isBackgroundSizeContain(backgroundImageSize)) {
    imageMoveDirection = isWidthScaleBigger
      ? headerBackgroundMoveDirections.horizontal
      : headerBackgroundMoveDirections.vertical;
  }

  return imageMoveDirection;
};

const calculateImageScale = (
  backgroundImageSize,
  imageMoveDirection,
  containerSize,
  imageSize,
) => {
  const { width: containerWidth, height: containerHeight } = containerSize;
  const { width, height } = imageSize;

  if (isBackgroundSizeCoverOrContain(backgroundImageSize)) {
    return isMoveDirectionVertical(imageMoveDirection)
      ? calculateWidthScale(containerWidth, width)
      : calculateHeightScale(containerHeight, height);
  }

  return 1;
};

const calculateMaxMoveValue = (
  backgroundImageSize,
  imageMoveDirection,
  containerSize,
  imageSize,
) => {
  const { width: containerWidth, height: containerHeight } = containerSize;
  const { width, height } = imageSize;
  const imageScale = calculateImageScale(
    backgroundImageSize,
    imageMoveDirection,
    containerSize,
    imageSize,
  );
  let maxMoveValue;

  if (isMoveDirectionVertical(imageMoveDirection)) {
    maxMoveValue = height * imageScale - containerHeight;
  } else if (isMoveDirectionHorizontal(imageMoveDirection)) {
    maxMoveValue = width * imageScale - containerWidth;
  }

  return Math.floor(maxMoveValue);
};

const calculateCoordinateForCover = (
  value,
  backgroundImageSize,
  imageMoveDirection,
  containerSize,
  imageSize,
) =>
  Math.min(
    Math.max(
      value,
      -calculateMaxMoveValue(
        backgroundImageSize,
        imageMoveDirection,
        containerSize,
        imageSize,
      ),
    ),
    0,
  );

const calculateCoordinateForContain = (
  value,
  backgroundImageSize,
  imageMoveDirection,
  containerSize,
  imageSize,
) =>
  Math.min(
    Math.max(value, 0),
    -calculateMaxMoveValue(
      backgroundImageSize,
      imageMoveDirection,
      containerSize,
      imageSize,
    ),
  );

const calculateCoordinate = (
  value,
  backgroundImageSize,
  imageMoveDirection,
  containerSize,
  imageSize,
) => {
  if (isBackgroundSizeCover(backgroundImageSize)) {
    return calculateCoordinateForCover(
      value,
      backgroundImageSize,
      imageMoveDirection,
      containerSize,
      imageSize,
    );
  }

  if (isBackgroundSizeContain(backgroundImageSize)) {
    return calculateCoordinateForContain(
      value,
      backgroundImageSize,
      imageMoveDirection,
      containerSize,
      imageSize,
    );
  }

  return value;
};

const calculateHeaderBackgroundStyles = (
  backgroundImageSize,
  imageMoveDirection,
  containerSize,
  imageSize,
  backgroundCoords,
) => {
  const { height, width } = imageSize;
  const { x, y } = backgroundCoords;
  const imageScale = calculateImageScale(
    backgroundImageSize,
    imageMoveDirection,
    containerSize,
    imageSize,
  );
  const positionY = toPx(
    calculateCoordinate(
      y,
      backgroundImageSize,
      imageMoveDirection,
      containerSize,
      imageSize,
    ),
  );
  const positionX = toPx(
    calculateCoordinate(
      x,
      backgroundImageSize,
      imageMoveDirection,
      containerSize,
      imageSize,
    ),
  );
  const isAnyDirection = isMoveDirectionAny(imageMoveDirection);

  const backgroundStyles = {
    backgroundPositionX: 0,
    backgroundPositionY: 0,
  };

  if (isBackgroundSizeCoverOrContain(backgroundImageSize)) {
    backgroundStyles.backgroundSize = `${toPx(width * imageScale)} ${toPx(
      height * imageScale,
    )}`;
  }

  if (isBackgroundSizeRepeat(backgroundImageSize)) {
    backgroundStyles.backgroundRepeat = 'repeat';
  }

  if (isMoveDirectionVertical(imageMoveDirection) || isAnyDirection) {
    backgroundStyles.backgroundPositionY = positionY;
  }

  if (isMoveDirectionHorizontal(imageMoveDirection) || isAnyDirection) {
    backgroundStyles.backgroundPositionX = positionX;
  }

  return backgroundStyles;
};

export {
  calculateCoordinate,
  isMoveDirectionVertical,
  isMoveDirectionHorizontal,
  calculateMoveDirection,
  calculateHeaderBackgroundStyles,
};
